import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useLeadMutations } from '../hooks';
import type { Matter } from '../types';

interface LeadDelegationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  /** Whether the current user may force-assign (Partner / MD). */
  canForce: boolean;
  onSuccess?: () => void;
}

export function LeadDelegationDialog({
  open,
  onOpenChange,
  matter,
  canForce,
  onSuccess,
}: LeadDelegationDialogProps) {
  const { t } = useTranslation();
  const [toUserId, setToUserId] = React.useState<number | undefined>(undefined);
  const [force, setForce] = React.useState(false);
  const { delegateMutation, forceAssignMutation } = useLeadMutations();

  React.useEffect(() => {
    if (open) {
      setToUserId(undefined);
      setForce(false);
    }
  }, [open]);

  const isPending = delegateMutation.isPending || forceAssignMutation.isPending;

  const handleConfirm = async () => {
    if (!matter || !toUserId) return;
    if (force && canForce) {
      await forceAssignMutation.mutateAsync({ id: matter.id, toUserId });
    } else {
      await delegateMutation.mutateAsync({ id: matter.id, toUserId });
    }
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('leadDelegation.reassignTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('leadDelegation.reassignDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('leadDelegation.newLeadLabel')}
            </label>
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={toUserId}
              params={{ status: 'ACTIVE', role_types: 'LEAD,PARTNER' }}
              onSelectItem={(item) =>
                setToUserId(item?.['id'] as number | undefined)
              }
              placeholder={t('leadDelegation.selectNewLead')}
            />
            <p className="mt-1 text-xs text-slate-400">{t('leadDelegation.eligibilityHint')}</p>
          </div>

          {canForce ? (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={force}
                onCheckedChange={(c) => setForce(c === true)}
                className="mt-0.5"
              />
              <span>
                {t('leadDelegation.forceLabel')}
                <span className="block text-xs text-slate-400">
                  {t('leadDelegation.forceHint')}
                </span>
              </span>
            </label>
          ) : null}
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!toUserId || isPending}>
            {isPending
              ? t('processing')
              : force && canForce
                ? t('leadDelegation.forceConfirm')
                : t('leadDelegation.delegateConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
